/**
 * The BAKED dictionary — SI/EN product-default UI copy, the `Dict` TYPE, and
 * (via scripts/build-theme-manifest.mjs) the flat key list that becomes the
 * per-portal override set customers edit in the admin `translations` table.
 *
 * WHAT YOU MAINTAIN HERE is the KEY STRUCTURE and TYPES — not the prose. On a
 * customized portal every key is overridden at runtime (overlayDict, applied via
 * getDict/useDict), so users NEVER see these baked strings: they are only the
 * fallback (no override configured, or backend unreachable) and the out-of-the-
 * box copy for a brand-new portal. Don't spend effort polishing this SI/EN prose
 * — copy polish happens in admin. The SI/EN parity here earns its keep by
 * catching a missing translation at COMPILE time and by seeding the manifest.
 *
 * KEYS ARE A CONTRACT. Each flattened key is stored against customer override
 * rows in the backend. Renaming or removing a key silently ORPHANS every
 * portal's override for it — audit-translations flags the dead rows, and the new
 * key renders baked until each admin re-edits. So add keys freely, but DEPRECATE
 * rather than rename/remove an existing one.
 */
import { isLangSegment, localeForSegment, segmentForLocale } from "./locale-map";

export { localeForSegment, segmentForLocale } from "./locale-map";

/**
 * A locale URL segment ("SI", "EN", "DE", …). Widened from the historical
 * "SI" | "EN" union for O8 runtime languages — which segments actually exist
 * is a PORTAL setting (`portal.availableLocales`, gated in the [lang] layout),
 * not a compile-time fact. The baked Dict still only ships SI/EN; every other
 * locale renders from the admin translations overlay with the baked default
 * as fallback (see `dict()` below and `getDict` in lib/i18n-server.ts).
 */
export type Lang = string;

/** Segments with a BAKED dictionary — the fallback authority, not the enabled
 *  set. EN-only since D19: English is the source language + universal fallback;
 *  every DELIVERY language (Slovenian included) comes from the backend
 *  `translations` overlay. The enabled set is `portal.availableLocales` (see
 *  the [lang] layout, which also always allows the portal's default locale). */
export const LANGS: Lang[] = ["EN"];

/** Shape check only ([A-Z]{2}) — the [lang] layout does the authoritative
 *  enabled-locale gate against `portal.availableLocales`. */
export function isLang(v: string | undefined | null): v is Lang {
  return isLangSegment(v);
}

export function defaultLang(): Lang {
  // RUNTIME env (read server-side only — `dict()`'s fallback + proxy.ts). The
  // `NEXT_PUBLIC_*` fallback is transitional; see lib/lecturehub/config.ts.
  const fromEnv = process.env.LECTUREHUB_LOCALE ?? process.env.NEXT_PUBLIC_LECTUREHUB_LOCALE ?? "";
  // Unset env → EN (D19): the template's neutral default. Every real portal
  // sets LECTUREHUB_LOCALE per deploy (sl_SI on the live SI portals).
  return fromEnv ? segmentForLocale(fromEnv) : "EN";
}

export function localeHeader(lang: Lang): string {
  return localeForSegment(lang);
}

/** BCP-47 tag for Intl APIs (PluralRules, RelativeTimeFormat, DisplayNames). */
export function localeTag(lang: Lang): string {
  const tag = localeHeader(lang).replace("_", "-");
  // Intl defaults Serbian to Cyrillic; the sr_RS portals write Latin script.
  return tag === "sr-RS" ? "sr-Latn-RS" : tag;
}

/**
 * Former Dict sections that are MARKETING, not chrome (D18 domain split).
 * Since O9 the marketing copy lives OUTSIDE this Dict entirely, as per-locale
 * landing-page DOCUMENTS (`content/home.<locale>.json`, rendered by
 * lib/pages) — the interim baked `lib/i18n-marketing.ts` is retired. So these
 * names can no longer match a Dict leaf. The list is kept as the overlay's
 * guard against STALE backend catalog rows (pre-split `home.*` seeds) ever
 * overriding authored template copy if a section were re-added.
 */
export const MARKETING_SECTIONS = ["home"] as const;

/**
 * CLDR plural forms for a countable unit noun. Slovenian selects all four
 * categories (one/two/few/other); English and German only ever select
 * one/other — their two/few values just repeat the plural and are never
 * selected by Intl.PluralRules. All four are required so the Dict type stays
 * locale-parity-checked and the theme manifest ships a complete row set.
 */
export type PluralForms = { one: string; two: string; few: string; other: string };

export type Dict = {
  nav: {
    courses: string;
    events: string;
    otts: string;
    podcasts: string;
    posts: string;
    news: string;
    authors: string;
    organizations: string;
    documents: string;
    login: string;
    register: string;
    profile: string;
    logout: string;
    skipToContent: string;
    mainNavigation: string;
    openMenu: string;
    closeMenu: string;
    collapse: string;
    expand: string;
    openSidebar: string;
    closeSidebar: string;
    /** @deprecated The header promo bar is authored in the navigation document
     *  (NavDoc.promo) — its label is content, not a translated UI string. Kept
     *  because renaming/removing a key orphans every portal's admin-edited
     *  translation rows for it (ecosystem rule; see CLAUDE.md i18n). */
    home: string;
  };
  appNav: {
    account: string;
    management: string;
    dashboard: string;
    catalog: string;
    learning: string;
    myProgress: string;
    overall: string;
    myProfile: string;
    helpSupport: string;
    adminPanel: string;
    reviewQueue: string;
    comingSoon: string;
    adminOnly: string;
    assistant: string;
    myLibrary: string;
    /** The section over the people directory. */
    people: string;
    enrolled: string;
    academy: string;
    resources: string;
    appNavigation: string;
    cart: string;
  };

  portal: {
    freePortalTitle: string;
    freePortalBody: string;
    freePortalHint: string;
    freePortalCta: string;
    payperBuyLabel: string;
    gatedUnavailable: string;
    authGateEyebrow: string;
    authGateTitle: string;
    authGateBody: string;
    authGateCta: string;
    authGateBodyTpl: string;
    authGateLoginWord: string;
    authGateRegisterWord: string;
  };
  /** Contact-form chrome (the Contact block). Labels are CHROME — translated
   *  through the overlay like every other UI string — while the heading, blurb
   *  and testimonial beside them are authored per page in the document. */
  contactForm: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    message: string;
    send: string;
    orEmailUs: string;
    noRecipient: string;
  };
  search: {
    title: string;
    /** Topbar box — must read as SEARCH, distinct from BrainAsk's "ask" promise
     *  (two boxes share the dashboard screen; their labels are the disambiguation). */
    placeholder: string;
    prompt: string;
    empty: string;
    askAssistant: string;
    /** SearchModal palette — the default "Enter" row landing on /search. */
    seeAll: string;
    suggestionsUnavailable: string;
    /** Zero-result state on /search. */
    emptyHint: string;
    /** The same, on a portal without the assistant (emptyHint names it). */
    emptyHintNoAssistant: string;
    browseCatalog: string;
    /** Guest pitch for the auth-gated assistant (the "ask" escalation must
     *  disclose the account requirement instead of springing a login wall). */
    aiPitchTitle: string;
    aiPitchBody: string;
    aiPitchCta: string;
  };
  assistant: {
    brandName: string;
    /** The guest landing at the assistant's own URL (app/[lang]/(public)/assistant):
     *  `{name}` is `brandName`, `{portal}` the portal's brand. A portal words the
     *  domain in its own rows — "Nia, vaša AI asistentka za nepremičnine". */
    landingTitleTpl: string;
    landingIntroTpl: string;
    trustLineTpl: string;
    createFreeAccount: string;
    haveAccount: string;
    signInToChat: string;
    openAssistant: string;
    /** "Try asking" — the questions are DERIVED from the portal's own content
     *  (its latest news, its topics, its newest items); these templates word
     *  them, and the `{…}` slot is set in bold. */
    tryAsking: string;
    askNewInTopicTpl: string;
    askSummariseTpl: string;
    askAboutTopicTpl: string;
    askAboutItemTpl: string;
    askAnythingTpl: string;
    /** "What does “{doc}” say?" — one of the portal's documents (the chat's starters). */
    askAboutDocTpl: string;
    /** "Who do I ask about {topic}?" — a directory topic (the chat's starters). */
    askWhoTpl: string;
    /** The chat's empty state: reveals the rest of the starter pool. */
    moreSuggestions: string;
    /** "What {name} knows": one sentence with the portal's topics in `{topics}`,
     *  then the live figures under count labels (Slovenian needs the counted
     *  form: "19 izobraževanj"), then where the knowledge comes from. */
    knowsTpl: string;
    knowsIntroTpl: string;
    knowledgeIncludes: string;
    countCourses: string;
    countVideos: string;
    countPodcasts: string;
    countEvents: string;
    countNews: string;
    groundedIn: string;
    fromDocs: string;
    fromExperts: string;
    /** The conversation preview: the derived question, the typing indicator,
     *  and real sources as chips. */
    previewLabel: string;
    typingTpl: string;
    sources: string;
    /** "How it works" — three titled steps. */
    howItWorks: string;
    stepAskTitle: string;
    stepAsk: string;
    stepSearchTitleTpl: string;
    stepSearchTpl: string;
    stepAnswerTitle: string;
    stepAnswer: string;
    /** The closing call. */
    finalCtaTitleTpl: string;
    finalCtaLead: string;
    pageTitle: string;
    greeting: string;
    helpPrompt: string;
    inputPlaceholder: string;
    send: string;
    newConversation: string;
    history: string;
    untitledConversation: string;
    /** Question-allowance copy. `{n}` / `{limit}` / `{when}` are filled at
     *  render time — a portal with no cap shows none of this. */
    questionsLeft: string;
    limitReachedTpl: string;
    limitResetsTpl: string;
    budgetExceeded: string;
    loadingChat: string;
    connectionError: string;
    /** The ways an answer fails, each with its own sentence (the composer's
     *  retry re-asks the same question). */
    answerInterrupted: string;
    messageTooLong: string;
    sessionExpired: string;
    signInAgain: string;
    retry: string;
    stop: string;
    jumpToLatest: string;
    /** History: a `?c=` thread the member no longer has, a read that failed,
     *  and the next page of the menu. */
    threadMissing: string;
    threadLoadFailed: string;
    showMore: string;
    notWiredYet: string;
    /** Shown instead of a reply that came back as raw retrieval context (O23). */
    answerWithheld: string;
    citationsModules: string;
    citationsDocs: string;
    citationsNews: string;
    citationsWeb: string;
    citationsPeople: string;
    /** Sixth citation bucket: files the admins uploaded only for the AI. */
    citationsKnowledgeFiles: string;
    /** Heading for the portal's OWN reviewed positions, which render above
     *  the sources because they are not sources — see AIChatApprovedKnowledge. */
    approvedKnowledgeTitle: string;
    /** Label for a statement the portal gave no scope of its own. */
    approvedKnowledgeFallbackScope: string;
    findAsk: string;
    preparingDownload: string;
    suggestions: [string, string, string, string];
    /** Lesson-scoped AI tutor widget (B19). */
    lessonTutor: {
      open: string;
      title: string;
      placeholder: string;
      intro: string;
      errorNotConfigured: string;
      errorNoContent: string;
      errorNotFound: string;
      errorGeneric: string;
    };
    /** The voice call (04-mutations §Voice). `{name}` is the assistant's name,
     *  `{minutes}` the call's maximum, `{time}` the countdown. */
    voice: {
      start: string;
      noteCost: string;
      consentTitle: string;
      consentBody: string;
      consentAccept: string;
      consentCancel: string;
      requestingMic: string;
      connecting: string;
      live: string;
      ending: string;
      you: string;
      timeLeft: string;
      end: string;
      micDenied: string;
      /** getUserMedia's other refusals: no device, a device another app
       *  holds, and a browser/page that cannot capture at all. */
      micNotFound: string;
      micBusy: string;
      micUnsupported: string;
      unavailable: string;
      limitAccount: string;
      limitPortal: string;
      limitPlatform: string;
      busy: string;
      endOther: string;
      setupFailed: string;
      retry: string;
      dismiss: string;
    };
  };
  learn: {
    exit: string;
    curriculum: string;
    closeCurriculum: string;
    noLessons: string;
    backToCourse: string;
    typeLecture: string;
    typeQuiz: string;
    typeArticle: string;
    typeAssignment: string;
    lessonOverview: string;
    subscribeToWatch: string;
    articleGated: string;
    passThreshold: string;
    points: string;
    completed: string;
    takeQuizBelow: string;
    subscribeToQuiz: string;
    graded: string;
    submitBelow: string;
    downloadBrief: string;
    /** @deprecated Subscription-only wording — wrong on single-payment portals.
     *  Superseded by `assignmentGated`. Kept (unused) because backend
     *  translation rows are keyed `(name, rel_theme_name)`: dropping or
     *  renaming the key silently orphans every portal's admin-edited cell. */
    subscribeToSubmit: string;
    /** Locked-lesson notices that are true on EVERY payment model — the
     *  `subscribeTo*` / `articleGated` keys above are kept for the backend
     *  translation rows (deprecate, don't rename) but no longer rendered: on a
     *  single-payment portal "Subscribe to watch" was simply wrong. */
    lockedLecture: string;
    freePreviewGated: string;
    lockedArticle: string;
    lockedQuiz: string;
    lockedAssignment: string;
    /** Payment-neutral replacement for `subscribeToSubmit`: the assignment form
     *  is gated until the viewer has access, however that access is bought. */
    assignmentGated: string;
    module: string;
    progress: string;
    current: string;
    lockedSr: string;
    finished: string;
    markFinished: string;
    /** Beside the disabled "Mark as finished": {s} = seconds left. */
    finishInTpl: string;
    badgeVideoLesson: string;
    badgeReading: string;
    badgeTask: string;
    minRead: string;
    previewingCourse: string;
    /** `{login}` is replaced by `saveProgressLogin` as a link. */
    saveProgressTpl: string;
    saveProgressLogin: string;
  };
  quiz: {
    results: string;
    score: string;
    points: string;
    passed: string;
    notPassed: string;
    submit: string;
    correct: string;
    incorrect: string;
    incorrectSr: string;
    yourPick: string;
    key: string;
    answerAll: string;
    retakeHint: string;
    retake: string;
    reviewNote: string;
    noKeyNote: string;
    /** "Attempted 2 times" — `attemptsCount` from the live portal schema. */
    attemptsMade: string;
    /** "Last attempt {date}" — the latest attempt's createdAt. */
    lastAttempt: string;
    /** A signed-in reader who has not started the course; the button says "Start". */
    startPrompt: string;
    singleHint: string;
    multiHint: string;
    noQuestions: string;
    complete: string;
  };
  assignment: {
    title: string;
    gradedTitle: string;
    graded: string;
    submit: string;
    update: string;
    placeholder: string;
    saved: string;
    score: string;
    points: string;
    comment: string;
    noComment: string;
    lockedNote: string;
    /** A signed-in reader who has not started the course; the button says "Start". */
    startPrompt: string;
    editableNote: string;
  };
  player: {
    videoUnavailable: string;
    loadFailed: string;
    /** The stream itself failed to play (the player loaded). */
    playbackFailed: string;
    /** The frame while the player bundle loads (screen readers). */
    loading: string;
    /** A click-to-play video facade's button (the Hero intro video); the
     *  video's title follows it for screen readers. */
    playVideo: string;
  };
  checkout: {
    paidProcessing: string;
    paidTimeout: string;
    cartTitle: string;
    kickerCourse: string;
    kickerEvent: string;
    kickerOtt: string;
    kickerPodcast: string;
    kickerFallback: string;
    emptyTitle: string;
    emptyBody: string;
    emptyCta: string;
    continueBrowsing: string;
    totalDue: string;
    securedByStripe: string;
    purchaseTerms: string;
    oneTimeNote: string;
    removeFromCart: string;
    /** Bulk clear (N05): the button, the question with `{n}` = items, its yes, its no. */
    clearCart: string;
    clearCartConfirmTpl: string;
    clearCartYes: string;
    keepItems: string;
    remove: string;
    stripeNotConfigured: string;
    proceedToPayment: string;
    accessActivates: string;
    addingToCart: string;
  };
  footer: {
    contact: string;
    information: string;
    about: string;
    aboutText: string;
    quickAccess: string;
    follow: string;
    cookies: string;
    terms: string;
    impressum: string;
    privacy: string;
    socialResponsibility: string;
    rightsPrefix: string;
  };
  consent: {
    message: string;
    accept: string;
    decline: string;
    learnMore: string;
    manage: string;
    /** The cookies page on a portal with no tracker that needs consent. */
    noTrackers: string;
  };
  posts: {
    listTitle: string;
    listTagline: string;
    empty: string;
    emptyHint: string;
    articleLabel: string;
    featured: string;
    readArticle: string;
    by: string;
    backToList: string;
    /** The gate in place of a withheld body (components/post/ArticleGate.tsx).
     *  REGISTER is an account, free; BUY / SUBSCRIBE are money — keep the copy
     *  saying which, never "log in" for a paid article. */
    gateRegisterEyebrow: string;
    gateRegisterTitle: string;
    gateRegisterBody: string;
    gateRegisterCta: string;
    gateHaveAccount: string;
    gateHaveAccess: string;
    gateSignIn: string;
    gateSubscribeEyebrow: string;
    gateSubscribeTitle: string;
    gateSubscribeBody: string;
    gateSubscribeCta: string;
    gateRestrictedTitle: string;
    gateRestrictedBody: string;
  };
  /** Paid-content disclosure on news and posts (`sponsorType` ≠ normal). The
   *  disclosure TEXT itself comes from the backend verbatim — regulator-set,
   *  never ours to word; these are only the chip labels around it. */
  sponsor: {
    sponsored: string;
    partner: string;
  };
  news: {
    listTitle: string;
    listTagline: string;
    empty: string;
    emptyHint: string;
    noMatches: string;
    noMatchesHint: string;
    searchLabel: string;
    searchPlaceholder: string;
    category: string;
    tag: string;
    allCategories: string;
    allTags: string;
    clear: string;
    results: string;
    newsLabel: string;
    announcementLabel: string;
    readMore: string;
    backToList: string;
    /** Card facts. Two different things (05-types): `pinned` decides ORDER,
     *  `featured` is editorial prominence — a post can be either without the other. */
    pinned: string;
    featured: string;
    /** The reader tools on an article (client, sticky under the hero). */
    textSize: string;
    textSmaller: string;
    textLarger: string;
    readAloud: string;
    stopReading: string;
    /** The list of an article's subheads (built from the body on the client). */
    contents: string;
    related: string;
    /** The CMS preview landing (app/[lang]/(public)/preview/[token]): the badge
     *  and the notice shown above an unpublished draft. */
    previewBadge: string;
    previewNotice: string;
  };
  /** Employee directory (opt-in `directory` module). */
  directory: {
    title: string;
    tagline: string;
    searchLabel: string;
    searchPlaceholder: string;
    allTeams: string;
    allTopics: string;
    team: string;
    topic: string;
    results: string;
    empty: string;
    emptyHint: string;
    clear: string;
    backToList: string;
    asksAbout: string;
    askPerson: string;
    contact: string;
    emailLabel: string;
    phoneLabel: string;
    mobileLabel: string;
    locationLabel: string;
    linkedinLabel: string;
    timezoneLabel: string;
    teachesLabel: string;
    viewProfile: string;
  };
  academy: {
    tagline: string;
  };
  pages: {
    /** Legal ad label on sponsored cards (CrossPromo block) — chrome, not an
     *  authored prop, so an author cannot blank the disclosure away. */
    adBadge: string;
    /** The unsold-slot mockup (CrossPromo): the reference renders empty ad
     *  space as a card advertising the slot itself. */
    adSpaceTitle: string;
    adSpaceHint: string;
    adSpaceMore: string;
    /** aria-label/tooltip on the banner's dismiss control. */
    adClose: string;
  };
  listing: {
    coursesTitle: string;
    eventsTitle: string;
    ottsTitle: string;
    podcastsTitle: string;
    authorsTitle: string;
    coursesTagline: string;
    eventsTagline: string;
    ottsTagline: string;
    podcastsTagline: string;
    authorsTagline: string;
    coursesEmpty: string;
    eventsEmpty: string;
    ottsEmpty: string;
    podcastsEmpty: string;
    authorsEmpty: string;
    authorsLoadError: string;
    instructorContent: string;
    /** The author page's news section — posts with them in the byline. */
    instructorNews: string;
    organizationsTitle: string;
    organizationsTagline: string;
    organizationsEmpty: string;
    organizationsLoadError: string;
    organizationContent: string;
    upcoming: string;
    archive: string;
    eventsUpcomingEmpty: string;
    featured: string;
    all: string;
    allCategories: string;
    noResults: string;
    clearFilters: string;
    previous: string;
    next: string;
    more: string;
    nothingToShow: string;
    viewMode: string;
    listView: string;
    gridView: string;
    libraryExplore: string;
    /** The news index's eyebrow — the news section, not the Academy. */
    newsExplore: string;
    viewLabel: string;
    gridShort: string;
    listShort: string;
  };
  documents: {
    title: string;
    tagline: string;
    empty: string;
    emptyHint: string;
    loadError: string;
    download: string;
    loginToDownload: string;
    preparing: string;
    unavailable: string;
  };
  detail: {
    free: string;
    live: string;
    upcoming: string;
    pastEvent: string;
    pastEventCta: string;
    enroll: string;
    enrolled: string;
    continueLearning: string;
    subscribe: string;
    like: string;
    liked: string;
    save: string;
    share: string;
    linkCopied: string;
    saved: string;
    similar: string;
    /** The author card's one action, under the article (`AuthorCards`). */
    allByAuthor: string;
    curriculum: string;
    prerequisites: string;
    syllabus: string;
    materials: string;
    faq: string;
    /** The article's cited sources, under its FAQ (ArticleSources). */
    sources: string;
    /** The link to the portal's editorial-policy page, under every article
     *  once the page is published (ArticleEndNote). */
    editorialPolicy: string;
    /** After a source's citation: `{date}` = when the editor accessed it. */
    sourceAccessedTpl: string;
    aboutCourse: string;
    aboutEvent: string;
    /** Heading of the author's summary + takeaways block (KeyPoints). */
    keyPoints: string;
    episodes: string;
    /** Toggle on the long-series episode strip (OttEpisodeStrip). */
    showAllEpisodes: string;
    showFewerEpisodes: string;
    /** The series stage's caption (N01, D32): `{n}` / `{total}` = episode position. */
    nowPlayingTpl: string;
    nextEpisode: string;
    /** Per-episode states on the list and the strip. */
    episodeLocked: string;
    episodeProcessing: string;
    episodeUnavailable: string;
    programme: string;
    chapters: string;
    trailer: string;
    processing: string;
    /** A published video with no file attached yet. */
    noVideoYet: string;
    paywallTitle: string;
    /** The paid item's paywall — any type, so it never names a course. */
    paidTitle: string;
    paidBody: string;
    paywallBody: string;
    /** `paywallBody` is the SUBSCRIPTION-model sentence; this is the
     *  single-payment one. PaywallCard and the lesson player pick by
     *  `portal.isSubscription`. */
    paywallBodySingle: string;
    buyCourse: string;
    scheduledBadge: string;
    scheduledOn: string;
    scheduledTitle: string;
    scheduledBody: string;
    location: string;
    starts: string;
    ends: string;
    eventWebsite: string;
    watchRecordings: string;
    recordings: string;
    levelPrefix: string;
    fallbackCourse: string;
    fallbackEvent: string;
    fallbackVideo: string;
    fallbackPodcast: string;
    fallbackArticle: string;
    fallbackNews: string;
    typeCourse: string;
    typeEvent: string;
    typePodcast: string;
    typeVideo: string;
    typeSeries: string;
    typeRecording: string;
    singleEpisode: string;
    continueCta: string;
    exploreCta: string;
    watchNow: string;
    listenNow: string;
    listenHeading: string;
    logInCta: string;
    start: string;
    signInToStart: string;
    subscribersOnly: string;
    enrolledStatus: string;
    oneTimeLifetime: string;
    comingSoonEyebrow: string;
    durationLabel: string;
    genreLabel: string;
    formatLabel: string;
    featuringLabel: string;
    featuringOne: string;
    publishedLabel: string;
    updatedLabel: string;
    instructorOne: string;
    instructorMany: string;
    organizationOne: string;
    organizationMany: string;
    whatIsIncluded: string;
    aboutContent: string;
    aboutRecording: string;
    whatsNew: string;
    recordingUnavailable: string;
    notARecording: string;
    prevItem: string;
    nextItem: string;
    noItems: string;
    sectionEmpty: string;
    curriculumUnpublished: string;
    lessonOne: string;
    lessonTwo: string;
    lessonFew: string;
    lessonOther: string;
  };
  auth: {
    signIn: string;
    showPassword: string;
    hidePassword: string;
    createAccount: string;
    forgotPassword: string;
    resetPassword: string;
    sendResetLink: string;
    newPassword: string;
    confirmPassword: string;
    currentPassword: string;
    changePassword: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    newsletterOptIn: string;
    termsAccept: string;
    termsRequired: string;
    allFieldsRequired: string;
    gdprAccept: string;
    gdprRequired: string;
    repeatPassword: string;
    passwordMismatch: string;
    haveAccount: string;
    noAccount: string;
    backToLogin: string;
    /** @deprecated 2026-09-09 — the sent state has its own heading + body now
     *  (`checkInbox*`). Kept unused: keys are never renamed or dropped
     *  (backend translation rows are keyed by name). */
    confirmationSent: string;
    /** Sent state, heading — the inbox is the next step. */
    checkInboxTitle: string;
    /** Sent state, body. `{email}` = the address they typed (rendered bold). */
    checkInboxBodyTpl: string;
    /** Sent state, small print: spam folder / start over. */
    checkInboxHint: string;
    /** Sent state, action: back to an empty form. */
    startOver: string;
    /** Sent state, footer lead-in before the sign-in link. */
    alreadyConfirmed: string;
    /** `userRegistrationType === "admin_confirmation"`: heading of the sent state. */
    pendingApprovalTitle: string;
    /** `userRegistrationType === "admin_confirmation"`: the second step, after the
     *  email — an admin approves before the first sign-in. */
    pendingApproval: string;
    /** `userRegistrationEnabled === false`: the form is not rendered at all. */
    registrationClosed: string;
    portalNotOpen: string;
    accountConfirmed: string;
    invalidLink: string;
    confirmAccountTitle: string;
    confirmSignInCta: string;
    passwordHint: string;
    resetSent: string;
    resetSuccess: string;
    badRecovery: string;
    signInSubtitle: string;
    registerSubtitle: string;
    forgotSubtitle: string;
    welcomePanelTitle: string;
    joinPanelTitle: string;
    forgotPanelTitle: string;
    perks: string[];
    /** Separates the social buttons from the email/password form. Rendered
     *  only when the portal has configured at least one provider. */
    socialDivider: string;
    /** Social button label. `{provider}` = the brand name, untranslated. */
    socialContinueWith: string;
  };
  profile: {
    title: string;
    overview: string;
    edit: string;
    security: string;
    subscriptions: string;
    delete: string;
    myLearning: string;
    mySaved: string;
    yourEnrolments: string;
    yourSaved: string;
    /** The line under the title on /saved. */
    savedDesc: string;
    /** The line under the title on /enrolled. */
    enrolledDesc: string;
    noEnrolments: string;
    noSaved: string;
    profileUpdated: string;
    passwordUpdated: string;
    deletionRequested: string;
    deletionCancelled: string;
    deleteWarning: string;
    deleteConfirm: string;
    typeDelete: string;
    requestDeletion: string;
    cancelDeletion: string;
    cancelPendingHint: string;
    noPendingDeletion: string;
    currentSubscription: string;
    noSubscription: string;
    seePlans: string;
    renewsOn: string;
    cancelAtPeriodEnd: string;
    cancelling: string;
    reinstate: string;
    active: string;
    inactive: string;
    plansTitle: string;
    chooseAPlan: string;
    perMonth: string;
    perYear: string;
    couponCode: string;
    orderSummary: string;
    discount: string;
    totalDue: string;
    payNow: string;
    continueToPayment: string;
    paymentFailed: string;
    oldTokensWarning: string;
    accountSections: string;
    unnamedAccount: string;
    newsletterOn: string;
    editShort: string;
    subUnlockPitch: string;
    accessEnds: string;
    memberSince: string;
    startHere: string;
    lessonsShort: string;
    filterPlaceholder: string;
    filterRecommendations: string;
    newest: string;
    noMatches: string;
    focusEnrolments: string;
    keepLearning: string;
    goodMorning: string;
    goodAfternoon: string;
    goodEvening: string;
    saveHint: string;
    discoverContent: string;
    enrolPrompt: string;
    browseCourses: string;
    accountEyebrow: string;
    accountDesc: string;
    editCardBody: string;
    subsCardBody: string;
    securityCardBody: string;
    deleteCardBody: string;
    editEyebrow: string;
    editDesc: string;
    yourDetails: string;
    keepCurrentTitle: string;
    keepCurrentBody: string;
    emailChangeTitle: string;
    emailChangeBody: string;
    emailChangeCta: string;
    helpTitle: string;
    helpBody: string;
    helpCta: string;
    newsletterHint: string;
    saving: string;
    saveChanges: string;
    securityEyebrow: string;
    securityDesc: string;
    /** Connected-accounts panel on the security page. Rendered only when the
     *  portal has configured a social provider. */
    signInMethodsTitle: string;
    signInMethodsBody: string;
    /** Row state. `{provider}` = the brand name. */
    signInConnected: string;
    signInNotConnected: string;
    signInConnect: string;
    signInDisconnect: string;
    /** Shown in place of "disconnect" when this is the only way in — the
     *  backend refuses to leave an account with no password and no provider. */
    signInSetPasswordFirst: string;
    signInNoPassword: string;
    importantEyebrow: string;
    signsOutTitle: string;
    passwordTipsTitle: string;
    passwordTipsBody: string;
    forgotTitle: string;
    forgotBody: string;
    forgotCta: string;
    hintMinChars: string;
    updating: string;
    deleteEyebrow: string;
    deleteDesc: string;
    permanentEyebrow: string;
    cannotUndoTitle: string;
    whatGetsRemoved: string;
    removedEnrolments: string;
    removedSaved: string;
    removedCredentials: string;
    keepCertificatesHint: string;
    deleteWord: string;
    confirmationLabel: string;
    inFieldBelow: string;
    working: string;
    membershipEyebrow: string;
    yourMembership: string;
    unlockAcademy: string;
    membershipManageDesc: string;
    membershipPitchDesc: string;
    subscribeThanks: string;
    /** `?success=1` but `activeSubscription` is still null — the Stripe webhook
     *  has not landed yet. The page polls; these two are what it shows meanwhile. */
    subscriptionActivating: string;
    subscriptionActivatingSlow: string;
    plansEyebrow: string;
    switchYourPlan: string;
    pickPlanHint: string;
    cancelledLabel: string;
    whatYouGet: string;
    everythingInOne: string;
    libraryTally: string;
    libraryEvents: string;
    libraryVideos: string;
    cancelAnytimeShort: string;
    membershipPerks: { title: string; body: string }[];
    trustBand: string[];
    faqEyebrow: string;
    faqTitle: string;
    membershipFaq: { q: string; a: string }[];
    readyWhenYouAre: string;
    pickPlanAbove: string;
    backToPlans: string;
    membershipLabel: string;
    noPlansConfigured: string;
    switchPlan: string;
    savePct: string;
    mostPopular: string;
    monthlyEquivalent: string;
    reinstatedNotice: string;
    cancelledNotice: string;
    planPriceUnavailable: string;
    stripePreviewOnly: string;
    payWithCard: string;
    addStripeKey: string;
    couponOptional: string;
  };
  /** Collections (#251) — /collections and /library/collections. `{portal}` is the
   *  portal's brand; `{n}` a number. */
  collections: {
    navLabel: string;
    typeLabel: string;
    indexTitle: string;
    indexDesc: string;
    groupedSection: string;
    orderedSection: string;
    itemCount: string;
    extras: string;
    seeAll: string;
    step: string;
    empty: string;
    emptyCollection: string;
  };
  /** FAQ (#250) — /faq and /library/faq. `{portal}` is the portal's brand. */
  faq: {
    navLabel: string;
    typeLabel: string;
    indexTitle: string;
    indexDesc: string;
    otherSection: string;
    related: string;
    allQuestions: string;
    openPage: string;
    signInToRead: string;
    notAvailable: string;
    empty: string;
    askAssistant: string;
    /** The index's search field (shown from six questions up). */
    searchPlaceholder: string;
    /** Nothing matched the typed query; `{q}` is the query. */
    noMatchTpl: string;
  };
  /** Issue reports — /profil/prijave (04-mutations "Issue reports"). `{portal}`
   *  is the portal's brand; `{n}` a count; `{name}` a file name. */
  issues: {
    reportProblem: string;
    navLabel: string;
    cardBody: string;
    eyebrow: string;
    listDesc: string;
    emptyTitle: string;
    emptyHint: string;
    newReport: string;
    updated: string;
    disabledNotice: string;
    formDesc: string;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    pageLabel: string;
    filesLabel: string;
    filesHint: string;
    filesLeft: string;
    addFiles: string;
    removeFile: string;
    fileType: string;
    fileCount: string;
    fileSize: string;
    descriptionRequired: string;
    submit: string;
    sending: string;
    sent: string;
    viewReport: string;
    closePanel: string;
    reportedOn: string;
    closedOn: string;
    you: string;
    team: string;
    noReplies: string;
    replyLabel: string;
    replyPlaceholder: string;
    replyRequired: string;
    sendReply: string;
    reopenHint: string;
    closedHint: string;
    backToList: string;
    download: string;
    category: Record<"BUG" | "CONTENT" | "FEATURE" | "QUESTION", string>;
    status: Record<"OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED", string>;
  };
  units: {
    course: PluralForms;
    lesson: PluralForms;
    section: PluralForms;
    episode: PluralForms;
    item: PluralForms;
    /** An ORDERED collection's numbered steps ("5 steps"). */
    step: PluralForms;
  };
  /** Error copy. friendlyMessage() (lib/lecturehub/errors.ts) REQUIRES this
   *  section, so every surfaced backend/validation error renders through the
   *  overlay (D19), never baked EN. */
  errors: {
    generic: string;
    genericBody: string;
    tryAgain: string;
    notFound: string;
    notFoundBody: string;
    backHome: string;
    languageNotAvailable: string;
    unauthorized: string;
    userPasswordMismatch: string;
    userDisabled: string;
    codeNotValid: string;
    codeConfirmed: string;
    passwordMismatch: string;
    alreadyEnrolled: string;
    courseAccessNotAllowed: string;
    assignmentGraded: string;
    quizSubmitFailed: string;
    moduleNotEnrollable: string;
    moduleRequiresPayment: string;
    moduleNotPurchasable: string;
    cartEmpty: string;
    moduleNotFound: string;
    moduleFree: string;
    moduleNoPrice: string;
    moduleAlreadyOwned: string;
    couponInvalid: string;
    couponExpired: string;
    subscriptionNotFound: string;
    subscriptionAlreadyCancelled: string;
    subscriptionNotCancelled: string;
    socialCancelled: string;
    socialStateInvalid: string;
    socialExchangeFailed: string;
    socialProviderDisabled: string;
    socialEmailMissing: string;
    socialAccountUnverified: string;
    socialIdentityTaken: string;
    socialLastCredential: string;
    issueReportsDisabled: string;
    issueReportNotFound: string;
    issueReportRateLimited: string;
    issueReportTooManyAttachments: string;
    emailRequired: string;
    emailPasswordRequired: string;
    nameSurnameRequired: string;
    passwordMinLength: string;
    passwordsDontMatch: string;
    accountExists: string;
  };
  journey: {
    welcomeAboard: string;
    welcomeBack: string;
    yourJourney: string;
    startsHere: string;
    inProgress: string;
    nextUpForYou: string;
    tagline: string;
    taglineExplore: string;
    startWithThese: string;
    recommendedSelection: string;
    activeCourses: string;
    moreInLibrary: string;
    continueModule: string;
    openModule: string;
    beginModule: string;
    seeAll: string;
    recommendedForYou: string;
    expandYourLibrary: string;
    alsoInLibrary: string;
    andThen: string;
  };
  dashboard: {
    latestNews: string;
    browseCatalog: string;
    completedLabel: string;
    allNews: string;
    newInAcademy: string;
    allDocuments: string;
    continueLearning: string;
    noNews: string;
    newThisWeek: string;
    featured: string;
    comingUp: string;
  };
  brain: {
    askTitle: string;
    askPlaceholder: string;
    askAction: string;
    suggestions: string[];
    knowledgeBase: string;
    recentlyAdded: string;
    documents: string;
    recordings: string;
    training: string;
    podcasts: string;
  };
};

const en: Dict = {
  nav: {
    courses: "Courses",
    events: "Events & conferences",
    otts: "Video library",
    podcasts: "Podcasts",
    posts: "Articles",
    news: "News",
    authors: "Instructors",
    organizations: "Organizations",
    documents: "Documents",
    login: "Sign in",
    register: "Sign up",
    profile: "My profile",
    logout: "Sign out",
    skipToContent: "Skip to content",
    mainNavigation: "Main navigation",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    collapse: "Collapse",
    expand: "Expand",
    openSidebar: "Open sidebar",
    closeSidebar: "Close sidebar",
    home: "Home",
  },
  appNav: {
    account: "Account",
    management: "Management",
    dashboard: "Dashboard",
    catalog: "Catalog",
    learning: "Learning",
    myProgress: "My Progress",
    overall: "Overall",
    myProfile: "My Profile",
    helpSupport: "Help & Support",
    adminPanel: "Admin Panel",
    reviewQueue: "Review Queue",
    comingSoon: "Coming soon",
    adminOnly: "Admin only",
    assistant: "AI Assistant",
    // The rail's names for the learner's own two pages (2026-09-18, the product
    // owner's): the enrolments are "my academy", the saved things "the library".
    myLibrary: "Library",
    enrolled: "My academy",
    people: "People",
    academy: "Academy",
    resources: "Resources",
    appNavigation: "App navigation",
    cart: "Cart",
  },

  contactForm: {
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phone: "Phone",
    message: "Message",
    send: "Send message",
    orEmailUs: "Or email us directly:",
    noRecipient: "No contact address is configured for this portal yet.",
  },
  search: {
    title: "Search",
    placeholder: "Search the catalog and news…",
    prompt: "Search the catalog — courses, events, videos, podcasts — and the news.",
    empty: "No results for your search.",
    askAssistant: "Ask the AI assistant instead",
    seeAll: "See all results",
    suggestionsUnavailable: "Suggestions couldn't load. Try again or open all results.",
    emptyHint: "Try a different word — or ask the AI assistant, which answers across all content.",
    emptyHintNoAssistant: "Try a different word, or browse the catalog.",
    browseCatalog: "Browse the catalog",
    aiPitchTitle: "Ask instead of searching",
    aiPitchBody:
      "The AI assistant answers questions across all courses, events, videos, and articles — free with an account.",
    aiPitchCta: "Create a free account and ask",
  },
  assistant: {
    brandName: "Knowly",
    landingTitleTpl: "{name}, your AI assistant",
    landingIntroTpl:
      "Ask anything. {name} combines the broad knowledge of leading AI models with {portal}'s own content and expertise — and shows you the sources when it uses portal content.",
    trustLineTpl: "{n} pieces of portal content · answers with sources",
    createFreeAccount: "Create a free account",
    haveAccount: "Already have an account?",
    signInToChat: "Sign in to chat",
    openAssistant: "Open the assistant",
    tryAsking: "Try asking",
    askNewInTopicTpl: "What's new in {topic}?",
    askSummariseTpl: "Summarise “{title}”",
    askAboutTopicTpl: "What do you have on {topic}?",
    askAboutItemTpl: "What will I learn from “{item}”?",
    askAnythingTpl: "Or ask {name} anything",
    askAboutDocTpl: "What does “{doc}” say?",
    askWhoTpl: "Who do I ask about {topic}?",
    moreSuggestions: "More suggestions",
    knowsTpl: "What {name} knows",
    knowsIntroTpl:
      "{name} can help with general questions and with specialist topics covered on the portal, including {topics} and many others.",
    knowledgeIncludes: "Its portal knowledge currently includes:",
    countCourses: "Courses",
    countVideos: "Videos",
    countPodcasts: "Podcasts",
    countEvents: "Events and conferences",
    countNews: "News",
    groundedIn: "From",
    fromDocs: "Documents",
    fromExperts: "Experts",
    previewLabel: "A conversation, previewed",
    typingTpl: "{name} is writing",
    sources: "Sources",
    howItWorks: "How it works",
    stepAskTitle: "Ask",
    stepAsk: "Ask in your own words.",
    stepSearchTitleTpl: "{name} searches",
    stepSearchTpl:
      "It finds relevant knowledge from the portal and combines it with the capabilities of leading AI models.",
    stepAnswerTitle: "Get the answer",
    stepAnswer: "You receive a clear answer with links to the relevant portal sources.",
    finalCtaTitleTpl: "Ask {name}",
    finalCtaLead: "Explore a topic, find something on the portal or simply ask a question.",
    pageTitle: "AI Assistant",
    greeting: "Hi, I'm",
    helpPrompt: "How can I help?",
    inputPlaceholder: "Ask me anything…",
    send: "Send message",
    newConversation: "New conversation",
    history: "History",
    untitledConversation: "Untitled conversation",
    questionsLeft: "{n} of {limit} questions left",
    limitReachedTpl: "You've used all {limit} of your questions for now.",
    limitResetsTpl: "The next one becomes available on {when}.",
    budgetExceeded: "AI chat is temporarily unavailable. Please try again later.",
    loadingChat: "Loading conversation…",
    connectionError: "Sorry — I couldn't get an answer just now.",
    answerInterrupted: "The answer was cut off before it finished.",
    messageTooLong: "That question is too long — keep it under 4,000 characters.",
    sessionExpired: "Your session has expired.",
    signInAgain: "Sign in again",
    retry: "Try again",
    stop: "Stop answering",
    jumpToLatest: "Jump to the latest",
    threadMissing: "That conversation is no longer available.",
    threadLoadFailed: "The conversation couldn't be loaded. Try again in a moment.",
    showMore: "Show more",
    notWiredYet:
      "The assistant is in development and not connected to content yet. UI is live; portal wiring follows.",
    answerWithheld: "That reply came back malformed, so it wasn’t shown. Please ask again.",
    citationsModules: "Content",
    citationsDocs: "Documents",
    citationsNews: "News",
    citationsWeb: "Web",
    citationsPeople: "People",
    citationsKnowledgeFiles: "Internal files",
    approvedKnowledgeTitle: "Our position",
    approvedKnowledgeFallbackScope: "Our position",
    findAsk: "Find or ask anything…",
    preparingDownload: "Preparing download…",
    // PORTAL-NEUTRAL ON PURPOSE (the auth panel's rule). These are the fill
    // behind the questions derived from the portal's own content (see
    // lib/assistant/derive-questions.ts) — the chat page and the landing wall
    // reach them only when a portal has published nothing yet — and they are
    // seeded into every new portal, so they must read true on an academy, a
    // newsroom and an intranet alike. They used to be fork #0's precious-metals
    // questions, which a media portal inherited (observed 2026-09-06 on N24).
    suggestions: [
      "What can you help me with?",
      "What's new on the portal?",
      "Recommend something to start with",
      "How do I find a specific topic?",
    ],
    lessonTutor: {
      open: "Ask about this lesson",
      title: "AI tutor for this content",
      placeholder: "Ask anything about this lesson…",
      intro:
        "Ask a question about this lesson — I answer from its material (the video, transcript, or text).",
      errorNotConfigured:
        "The AI tutor isn't set up for this portal yet. The interface is live; wiring follows.",
      errorNoContent:
        "This lesson has no readable content to ground an answer on yet (e.g. a video transcript).",
      errorNotFound: "This content can't be found, or you don't have access to it.",
      errorGeneric:
        "Sorry — I'm having connection trouble right now. Please try again in a moment.",
    },
    voice: {
      start: "Talk to {name}",
      noteCost: "Each spoken question counts as one AI question.",
      consentTitle: "Talk to {name} by voice",
      consentBody:
        "Your voice is sent to OpenAI to be understood and answered. Each spoken question counts as one AI question, like a typed one. A call ends by itself after {minutes} minutes.",
      consentAccept: "I agree — start the call",
      consentCancel: "Not now",
      requestingMic: "Waiting for microphone access…",
      connecting: "Connecting…",
      live: "In a call with {name}",
      ending: "Ending the call…",
      you: "You",
      timeLeft: "{time} left",
      end: "End call",
      micDenied: "The microphone is blocked. Allow it in your browser and try again.",
      micNotFound: "No microphone was found. Connect one and try again.",
      micBusy: "Another app is using the microphone. Close it and try again.",
      micUnsupported: "This browser can't use a microphone on this page.",
      unavailable: "Voice is not available right now.",
      limitAccount: "You have used today's voice time.",
      limitPortal: "The portal's voice time for this month is used up.",
      limitPlatform: "Too many calls right now — please try again in a moment.",
      busy: "You already have a call open, here or in the editor.",
      endOther: "End the other call",
      setupFailed: "The call could not be set up. Please try again.",
      retry: "Try again",
      dismiss: "Close",
    },
  },
  portal: {
    freePortalTitle: "All content is free",
    freePortalBody:
      "This academy runs without subscriptions. The entire library is open to every signed-in member.",
    freePortalHint:
      "No payments, no plans. Just explore the catalog and enroll in whatever interests you.",
    freePortalCta: "Explore the library",
    payperBuyLabel: "Add to cart",
    gatedUnavailable: "Currently unavailable",
    authGateEyebrow: "Account required",
    authGateTitle: "Log in to access",
    authGateBody:
      "You need a free account to view this content. Free courses still require registration.",
    authGateCta: "Log in",
    authGateBodyTpl:
      "You need to {login} to view this content. If you do not yet have an account, {register}.",
    authGateLoginWord: "log in",
    authGateRegisterWord: "register",
  },
  learn: {
    exit: "Close",
    curriculum: "Curriculum",
    closeCurriculum: "Close curriculum",
    noLessons: "This course has no lessons yet.",
    backToCourse: "Back to course",
    typeLecture: "Lecture",
    typeQuiz: "Quiz",
    typeArticle: "Article",
    typeAssignment: "Assignment",
    lessonOverview: "Lesson Overview",
    subscribeToWatch: "Subscribe to watch this lecture.",
    articleGated: "The full article is available with an active subscription.",
    passThreshold: "Pass",
    points: "points",
    completed: "Completed",
    takeQuizBelow: "Take the quiz below.",
    subscribeToQuiz: "Subscribe to take this quiz.",
    graded: "Graded",
    submitBelow: "Submit your work below.",
    downloadBrief: "Download the brief (PDF)",
    subscribeToSubmit: "Subscribe to submit this assignment.",
    lockedLecture: "This lecture is locked until you unlock the course.",
    freePreviewGated: "Preview not yet available — this lesson unlocks with access to the course.",
    lockedArticle: "The full article is locked until you unlock the course.",
    lockedQuiz: "This quiz is locked until you unlock the course.",
    lockedAssignment: "This assignment is locked until you unlock the course.",
    assignmentGated: "Submitting this assignment requires access to this course.",
    module: "Module",
    progress: "Progress",
    current: "Current",
    lockedSr: "locked",
    finished: "Finished",
    markFinished: "Mark as Finished",
    finishInTpl: "You can mark it finished in {s} s",
    badgeVideoLesson: "Video Lesson",
    badgeReading: "Reading",
    badgeTask: "Task",
    minRead: "5 min read",
    previewingCourse: "You're previewing this course.",
    saveProgressTpl: "{login} so we can save your progress.",
    saveProgressLogin: "Sign in",
  },
  quiz: {
    results: "Your results",
    score: "Score",
    points: "points",
    passed: "Passed",
    notPassed: "Not passed",
    submit: "Submit answers",
    correct: "Correct",
    incorrect: "Incorrect",
    incorrectSr: "Incorrect",
    yourPick: "Your answer",
    key: "Correct answer",
    answerAll: "Answer every question to submit.",
    retakeHint: "You can retake this quiz as many times as you like.",
    retake: "Try again",
    reviewNote: "Your answers are graded below.",
    noKeyNote: "Showing your most recent attempt.",
    attemptsMade: "Attempted {count}×",
    lastAttempt: "Last attempt {date}",
    startPrompt: "Start the course to take the quiz.",
    singleHint: "Pick one answer.",
    multiHint: "Pick all that apply.",
    noQuestions: "This quiz has no questions yet.",
    complete: "Quiz Complete",
  },
  assignment: {
    title: "Your answer",
    gradedTitle: "Submission Graded",
    graded: "Graded",
    submit: "Submit answer",
    update: "Update answer",
    placeholder: "Write your answer here…",
    saved: "Answer saved.",
    score: "Score",
    points: "points",
    comment: "Instructor comment",
    noComment: "No comment from the instructor.",
    lockedNote: "This answer has been graded and can no longer be edited.",
    startPrompt: "Start the course to submit your answer.",
    editableNote: "You can edit and resubmit your answer until it is graded.",
  },
  player: {
    videoUnavailable: "Video unavailable.",
    loadFailed: "The video player failed to load.",
    loading: "Loading the video player",
    playbackFailed: "This video can't be played right now.",
    playVideo: "Play the video",
  },
  checkout: {
    paidProcessing:
      "Payment received — activating your access. This page will refresh automatically.",
    paidTimeout:
      "Activation is taking longer than usual. If access isn't unlocked within a minute, contact support.",
    cartTitle: "Your cart",
    kickerCourse: "Course",
    kickerEvent: "Live event",
    kickerOtt: "Video",
    kickerPodcast: "Podcast",
    kickerFallback: "Module",
    emptyTitle: "Your cart is empty",
    emptyBody: "Browse the catalog and add a course to get started.",
    emptyCta: "Browse courses",
    continueBrowsing: "Continue browsing",
    totalDue: "Total due",
    securedByStripe: "Secured by Stripe",
    purchaseTerms: "Purchase terms",
    oneTimeNote: "One-time payment. No subscription. Lifetime access.",
    removeFromCart: "Remove from cart",
    clearCart: "Clear cart",
    clearCartConfirmTpl: "Remove everything from your cart ({n})?",
    clearCartYes: "Yes, clear the cart",
    keepItems: "Keep items",
    remove: "Remove",
    stripeNotConfigured:
      "Online payments are disabled because the portal's Stripe key isn't configured.",
    proceedToPayment: "Proceed to payment",
    accessActivates: "Access activates automatically once your payment confirms.",
    addingToCart: "Adding to your cart…",
  },
  footer: {
    contact: "Contact",
    information: "Information",
    about: "About us",
    // Brand-NEUTRAL on purpose. This value is the template default and it seeds
    // every new portal's translation rows (D19), so a tenant-specific "about"
    // paragraph here becomes that company's description on someone else's
    // footer — which is exactly what shipped (roadmap O12). Each portal
    // overwrites this with its own text in portal-admin.
    aboutText: "Learn at your own pace, with courses, video and podcasts in one place.",
    quickAccess: "Quick access",
    follow: "Follow us",
    cookies: "Cookies",
    terms: "Terms",
    impressum: "Imprint",
    privacy: "Privacy policy",
    /** @deprecated Unused since 2026-09-12 — the "socially responsible employer"
     *  page was fork #0's, not the theme's (O57); kept so the backend rows keyed
     *  on it aren't orphaned. */
    socialResponsibility: "Socially responsible employer",
    /** @deprecated Unused (no consumer) — kept so the backend rows keyed on it
     *  aren't orphaned. Neutral value so it can't leak a tenant if ever wired. */
    rightsPrefix: "All rights reserved",
  },
  consent: {
    message:
      "We use analytics cookies to understand how the site is used. They load only with your consent.",
    accept: "Accept",
    decline: "Decline",
    learnMore: "Learn more",
    manage: "Change cookie preferences",
    noTrackers:
      "This site sets no analytics or marketing cookies, only the ones it needs to work, so there is nothing to change.",
  },
  posts: {
    listTitle: "Articles",
    listTagline: "Articles and insights.",
    empty: "No articles published yet.",
    emptyHint: "New articles will show up here as soon as they're published.",
    articleLabel: "Article",
    featured: "Featured",
    readArticle: "Read article",
    by: "By",
    backToList: "Back to articles",
    gateRegisterEyebrow: "Free for registered readers",
    gateRegisterTitle: "Sign in to read the whole article",
    gateRegisterBody: "The rest of this article is for registered readers. An account is free.",
    gateRegisterCta: "Create a free account",
    gateHaveAccount: "Already registered?",
    gateHaveAccess: "Already have access?",
    gateSignIn: "Sign in",
    gateSubscribeEyebrow: "Subscribers only",
    gateSubscribeTitle: "This article is for subscribers",
    gateSubscribeBody: "Subscribe to read the whole article.",
    gateSubscribeCta: "Subscribe",
    gateRestrictedTitle: "This article isn't available to your account",
    gateRestrictedBody: "The rest of this article is limited to selected readers.",
  },
  sponsor: {
    sponsored: "Sponsored",
    partner: "Partner content",
  },
  news: {
    listTitle: "News & announcements",
    listTagline: "News, announcements and updates.",
    empty: "No news published yet.",
    emptyHint: "News and announcements will show up here as soon as they're published.",
    // Distinct from `empty` on purpose: "nothing published" and "your filter
    // matched nothing" are different facts, and telling a reader who just
    // searched that the portal has no news would be a lie.
    noMatches: "No matches",
    noMatchesHint: "Try a different word, category or tag.",
    searchLabel: "Search news",
    searchPlaceholder: "Search headlines and article text\u2026",
    category: "Category",
    tag: "Tag",
    allCategories: "All categories",
    allTags: "All tags",
    clear: "Clear filters",
    results: "articles",
    newsLabel: "News",
    announcementLabel: "Announcement",
    readMore: "Read more",
    backToList: "Back to news",
    pinned: "Pinned",
    featured: "Featured",
    textSize: "Text size",
    textSmaller: "Smaller text",
    textLarger: "Larger text",
    readAloud: "Read aloud",
    stopReading: "Stop reading",
    contents: "In this article",
    related: "Related",
    previewBadge: "Preview",
    previewNotice:
      "An unpublished draft, shown through a preview link. The link is the only thing protecting it — don't forward it.",
  },
  directory: {
    title: "People",
    tagline: "Find a colleague by name, team, or the topic they look after.",
    searchLabel: "Search people",
    searchPlaceholder: "Search by name, role, team or topic…",
    allTeams: "All teams",
    allTopics: "All topics",
    team: "Team",
    topic: "Topic",
    results: "people",
    empty: "No matches",
    emptyHint: "Try a different name, team or topic.",
    clear: "Clear filters",
    backToList: "Back to people",
    asksAbout: "Ask about",
    // The dashboard band's title. Deliberately contrasts with the masthead's
    // "Ask Knowly": that one asks the assistant, this one routes you to a
    // colleague who owns the topic.
    askPerson: "Ask a real person",
    contact: "Contact",
    emailLabel: "Email",
    phoneLabel: "Phone",
    mobileLabel: "Mobile",
    locationLabel: "Location",
    linkedinLabel: "LinkedIn",
    timezoneLabel: "Timezone",
    teachesLabel: "Also teaches",
    viewProfile: "View profile",
  },
  academy: {
    // The page's whole thesis in one line, and it is not new — AppLeftNav has
    // said it in a comment since the Academy nav group existed: "a course is a
    // guided path, an article is knowledge you read". Saying it out loud is
    // what makes /academy read as a section rather than two grids on a page.
    tagline: "A course is a guided path. An article is knowledge you read.",
  },
  pages: {
    adBadge: "Ad",
    adSpaceTitle: "Advertising space",
    adSpaceHint: "Your promotion could live here.",
    adSpaceMore: "More information",
    adClose: "Close ad",
  },
  listing: {
    coursesTitle: "Courses",
    // A listing is titled as the rail names it.
    eventsTitle: "Events & conferences",
    ottsTitle: "Video library",
    podcastsTitle: "All podcasts",
    authorsTitle: "Instructors & experts",
    coursesTagline: "Structured learning with lessons, quizzes and tasks.",
    eventsTagline: "Recordings from conferences, webinars and live events.",
    ottsTagline: "On-demand video and series — watch any time.",
    podcastsTagline: "Audio episodes and series for listening on the go.",
    authorsTagline: "The instructors and experts behind the content on the platform.",
    coursesEmpty: "No courses available yet.",
    eventsEmpty: "No events scheduled yet.",
    ottsEmpty: "No videos in the library yet.",
    podcastsEmpty: "No podcasts published yet.",
    authorsEmpty: "The instructors directory is empty right now.",
    authorsLoadError:
      "Couldn’t load the instructors directory right now. Please try again shortly.",
    instructorContent: "Content by this instructor",
    instructorNews: "News by this author",
    organizationsTitle: "Organizations",
    organizationsTagline: "The organizations behind the content on the platform.",
    organizationsEmpty: "The organizations directory is empty right now.",
    organizationsLoadError:
      "The organizations directory can't be loaded right now. Please try again in a moment.",
    organizationContent: "Content from this organization",
    upcoming: "Upcoming",
    archive: "Archive",
    eventsUpcomingEmpty: "No upcoming events scheduled.",
    featured: "Featured",
    all: "All",
    allCategories: "All categories",
    noResults: "No results for the selected filters.",
    clearFilters: "Clear filters",
    previous: "← Previous",
    next: "Next →",
    more: "More",
    nothingToShow: "Nothing to show.",
    viewMode: "View mode",
    listView: "List view",
    gridView: "Grid view",
    // The section the page belongs to, then the verb. "Library" clashed with the
    // rail item of that name (the saved things) once it existed.
    libraryExplore: "Academy · Explore",
    newsExplore: "News · Explore",
    viewLabel: "View",
    gridShort: "Grid",
    listShort: "List",
  },
  documents: {
    title: "Documents",
    tagline: "Reference documents and resources to download.",
    empty: "No documents have been published yet.",
    emptyHint: "Documents will show up here as soon as they're published.",
    loadError: "Couldn’t load the documents right now. Please try again shortly.",
    download: "Download",
    loginToDownload: "Log in to download",
    preparing: "Preparing…",
    unavailable: "This document is no longer available.",
  },
  detail: {
    free: "Free",
    live: "Live",
    upcoming: "Upcoming",
    pastEvent: "Past event",
    pastEventCta: "This event has ended.",
    enroll: "Enroll",
    enrolled: "Enrolled ✓",
    continueLearning: "Continue learning →",
    subscribe: "Subscribe to view",
    like: "Like",
    liked: "Liked",
    save: "Save",
    share: "Share",
    linkCopied: "Link copied",
    saved: "Saved",
    similar: "You may also like",
    allByAuthor: "All by this author",
    curriculum: "Curriculum",
    prerequisites: "Prerequisites",
    syllabus: "Syllabus",
    materials: "Materials",
    faq: "FAQ",
    sources: "Sources",
    editorialPolicy: "Editorial policy",
    sourceAccessedTpl: "accessed {date}",
    aboutCourse: "About the course",
    keyPoints: "Key points",
    aboutEvent: "About the event",
    episodes: "Episodes",
    showAllEpisodes: "Show all episodes",
    showFewerEpisodes: "Show fewer",
    nowPlayingTpl: "Episode {n} of {total}",
    nextEpisode: "Next episode",
    episodeLocked: "Locked",
    episodeProcessing: "Processing",
    episodeUnavailable: "Unavailable",
    programme: "Programme",
    chapters: "Chapters",
    trailer: "Trailer",
    processing: "Video is still processing. Please try again later.",
    noVideoYet: "This video isn't available yet.",
    paywallTitle: "Members-only content",
    paidTitle: "Paid content",
    paidBody: "Unlock it with a one-time purchase.",
    paywallBody: "Unlock this with any subscription plan.",
    paywallBodySingle: "Unlock this by purchasing the course.",
    buyCourse: "Buy the course",
    scheduledBadge: "Coming soon",
    scheduledOn: "Available",
    scheduledTitle: "This content is coming soon",
    scheduledBody: "This content is scheduled and not published yet. Check back on the date shown.",
    location: "Location",
    starts: "Starts",
    ends: "Ends",
    eventWebsite: "Event website ↗",
    watchRecordings: "Watch recordings",
    recordings: "Recordings",
    levelPrefix: "Level",
    fallbackCourse: "Course",
    fallbackEvent: "Event",
    fallbackVideo: "Video",
    fallbackPodcast: "Podcast",
    fallbackArticle: "Article",
    fallbackNews: "News",
    typeCourse: "Course",
    typeEvent: "Event",
    typePodcast: "Podcast",
    typeVideo: "Video",
    typeSeries: "Series",
    typeRecording: "Recording",
    singleEpisode: "Single episode",
    continueCta: "Continue",
    exploreCta: "Explore",
    watchNow: "Watch now",
    listenNow: "Listen now",
    listenHeading: "Listen",
    logInCta: "Log in",
    start: "Start",
    signInToStart: "Sign in to start",
    subscribersOnly: "Subscribers only",
    enrolledStatus: "Enrolled",
    oneTimeLifetime: "One-time · lifetime access",
    comingSoonEyebrow: "Coming soon",
    durationLabel: "Duration",
    genreLabel: "Genre",
    formatLabel: "Format",
    featuringLabel: "Featuring",
    featuringOne: "Featuring",
    publishedLabel: "Published",
    updatedLabel: "Updated",
    instructorOne: "Instructor",
    instructorMany: "Instructors",
    organizationOne: "Organization",
    organizationMany: "Organizations",
    whatIsIncluded: "What is included",
    aboutContent: "About",
    aboutRecording: "About this recording",
    whatsNew: "What's new",
    recordingUnavailable: "Recording unavailable.",
    notARecording: "Not a recording",
    prevItem: "Previous",
    nextItem: "Next",
    noItems: "No items.",
    sectionEmpty: "No items in this section yet.",
    curriculumUnpublished: "Curriculum hasn't been published yet.",
    lessonOne: "Lesson",
    lessonTwo: "Lessons",
    lessonFew: "Lessons",
    lessonOther: "Lessons",
  },
  auth: {
    signIn: "Sign in",
    showPassword: "Show password",
    hidePassword: "Hide password",
    createAccount: "Create your account",
    forgotPassword: "Forgot your password?",
    resetPassword: "Reset your password",
    sendResetLink: "Send reset link",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    currentPassword: "Current password",
    changePassword: "Change password",
    email: "Email",
    password: "Password",
    firstName: "First name",
    lastName: "Last name",
    newsletterOptIn: "Send me product updates and new content alerts.",
    termsAccept: "I agree to the Terms and Cookie policy.",
    termsRequired: "You must accept the Terms to continue.",
    allFieldsRequired: "All fields are required.",
    gdprAccept: "I consent to the processing of my personal data per the Privacy Policy.",
    gdprRequired: "You must accept the data-processing consent to continue.",
    repeatPassword: "Repeat password",
    passwordMismatch: "Passwords do not match.",
    haveAccount: "Already have an account?",
    noAccount: "Don’t have an account?",
    backToLogin: "Back to sign in",
    confirmationSent: "Almost there. We sent a confirmation link to your email.",
    checkInboxTitle: "Check your inbox",
    checkInboxBodyTpl:
      "We’ve sent a confirmation link to {email}. Open it to activate your account, then sign in.",
    checkInboxHint:
      "Nothing there after a minute? Look in the spam folder, or start over with a different address.",
    startOver: "Use a different address",
    alreadyConfirmed: "Already confirmed your address?",
    pendingApprovalTitle: "Your request is in",
    pendingApproval:
      "An administrator also needs to approve your account. We’ll email you as soon as you can sign in.",
    registrationClosed:
      "This portal does not accept new registrations. If you should have access, ask the portal administrator for an account.",
    portalNotOpen: "This portal is not open yet. Only its administrators can sign in for now.",
    accountConfirmed: "Your account is confirmed.",
    invalidLink: "This link is no longer valid.",
    confirmAccountTitle: "Confirm account",
    confirmSignInCta: "Sign in to start learning.",
    passwordHint: "At least 8 characters.",
    resetSent: "If that email is registered, we’ve sent a reset link.",
    resetSuccess: "Your password has been updated.",
    badRecovery: "This reset link is no longer valid.",
    signInSubtitle: "Sign in to continue learning.",
    registerSubtitle: "Takes a minute. We’ll email you a link to confirm your address.",
    forgotSubtitle: "Enter the email tied to your account. We’ll send you a reset link.",
    // PORTAL-NEUTRAL ON PURPOSE (roadmap O12's rule, applied to copy). These
    // defaults are SEEDED into every new portal via the theme manifest, so
    // whatever stands here becomes some other tenant's first impression. They
    // used to read "Welcome back to the Academy." / "Start your path to
    // financial freedom." / three subscription perks — fork #0's brand and its
    // investment positioning, published on the sign-in page of every portal
    // that never overrode them (observed 2026-08-21 on a logistics intranet
    // whose staff buy nothing). The brand name is already on this screen as the
    // portal's own logo/wordmark, so the heading does not need to repeat it.
    welcomePanelTitle: "Welcome back.",
    joinPanelTitle: "Good to have you here.",
    forgotPanelTitle: "Let’s get you back in.",
    socialDivider: "or",
    socialContinueWith: "Continue with {provider}",
    // EMPTY BY DESIGN — a benefit list is a promise, and the template cannot
    // know what a given portal offers. A portal states its own (admin →
    // translations) or the panel renders none. Same rule as the PORTAL_*
    // identity vars: unset renders nothing rather than fork #0's answer.
    perks: [] as string[],
  },
  profile: {
    title: "My profile",
    overview: "Overview",
    edit: "Edit profile",
    security: "Password",
    subscriptions: "Subscription",
    delete: "Delete account",
    myLearning: "Learning",
    mySaved: "Saved",
    yourEnrolments: "My academy",
    yourSaved: "Library",
    savedDesc: "Saved and favourite content",
    enrolledDesc: "Your trainings and assigned content",
    noEnrolments: "You haven’t enrolled in anything yet.",
    // No glyph: the save control is a bookmark, the heart on a start card is a
    // LIKE, and "☆" was neither. The hint under it names the bookmark.
    noSaved: "Nothing saved yet.",
    profileUpdated: "Profile updated.",
    passwordUpdated: "Password updated.",
    deletionRequested:
      "Your account is queued for deletion. You can cancel this until it’s processed.",
    deletionCancelled: "Deletion request cancelled.",
    deleteWarning:
      "Deletion is asynchronous: your account remains usable until processed. You can cancel the request in that window.",
    // A LEAD-IN, not a sentence: DeleteForm renders `{deleteConfirm} [deleteWord]
    // {inFieldBelow}`. It used to be a full sentence naming the word itself, which in
    // Slovenian told the member to type DELETE while only IZBRIŠI enabled the button.
    deleteConfirm: "To confirm, type",
    typeDelete: "Type DELETE to confirm.",
    requestDeletion: "Request deletion",
    cancelDeletion: "Cancel deletion",
    cancelPendingHint:
      "Already requested deletion? You can cancel it here until it’s processed — from any device.",
    noPendingDeletion: "There is no pending deletion request on your account.",
    currentSubscription: "Current subscription",
    noSubscription: "You don’t have an active subscription.",
    seePlans: "See plans",
    renewsOn: "Renews on",
    cancelAtPeriodEnd: "Cancel at period end",
    cancelling: "Cancelling",
    reinstate: "Reinstate subscription",
    active: "Active",
    inactive: "Inactive",
    plansTitle: "Choose your plan",
    chooseAPlan: "Choose",
    perMonth: "month",
    perYear: "year",
    couponCode: "Coupon",
    orderSummary: "Order summary",
    discount: "Discount",
    totalDue: "Total due",
    payNow: "Pay now",
    continueToPayment: "Continue to payment",
    paymentFailed: "Payment failed.",
    oldTokensWarning:
      "Changing your password does NOT sign out other devices. Existing sessions remain active until their token expires.",
    accountSections: "Account sections",
    unnamedAccount: "Unnamed account",
    newsletterOn: "Newsletter on",
    editShort: "Edit",
    subUnlockPitch:
      "Unlock the full library — courses, podcasts, videos and events — with a single plan.",
    accessEnds: "Access ends",
    memberSince: "Member since",
    startHere: "Start here",
    lessonsShort: "lessons",
    filterPlaceholder: "Filter…",
    filterRecommendations: "Filter recommendations",
    newest: "Newest",
    noMatches: "No matches.",
    focusEnrolments: "Your enrolments",
    keepLearning: "Continue learning.",
    goodMorning: "Good morning",
    goodAfternoon: "Good afternoon",
    goodEvening: "Good evening",
    saveHint: "Tap the bookmark on any module to save it for later.",
    discoverContent: "Discover content",
    enrolPrompt: "Enrol in a course to start learning — it'll show up here.",
    browseCourses: "Browse courses",
    accountEyebrow: "Account · I",
    accountDesc: "Your profile, subscription, security and account controls.",
    editCardBody: "Update your name and newsletter preferences.",
    subsCardBody: "Manage your plan, billing and renewal.",
    securityCardBody: "Change the password used to sign in.",
    deleteCardBody: "Permanently remove your account and content.",
    editEyebrow: "Account · VI",
    editDesc:
      "Update the name we show on certificates and emails, and choose whether to receive our newsletter.",
    yourDetails: "Your details",
    keepCurrentTitle: "Keep your profile current.",
    keepCurrentBody:
      "Your name appears on certificates, course welcome emails and inside the academy.",
    emailChangeTitle: "Email can't be changed here",
    emailChangeBody:
      "To change your sign-in email contact support — we'll verify and switch it for you.",
    emailChangeCta: "Contact support",
    helpTitle: "Need a hand?",
    helpBody: "We answer most account questions within one working day.",
    helpCta: "Get in touch",
    newsletterHint:
      "Occasional updates about new courses, podcasts and events. Unsubscribe anytime.",
    saving: "Saving…",
    saveChanges: "Save changes",
    securityEyebrow: "Security · VII",
    securityDesc:
      "Update the password used to sign in. Keep it long, unique, and stored in a manager.",
    signInMethodsTitle: "Sign-in methods",
    signInMethodsBody: "Connect an account to sign in with one tap instead of a password.",
    signInConnected: "Connected",
    signInNotConnected: "Not connected",
    signInConnect: "Connect {provider}",
    signInDisconnect: "Disconnect",
    signInSetPasswordFirst:
      "This is the only way into your account. Set a password before disconnecting it.",
    signInNoPassword: "No password set — you sign in with a connected account.",
    importantEyebrow: "Important",
    signsOutTitle: "Changing your password signs you out elsewhere.",
    passwordTipsTitle: "Strong password tips",
    passwordTipsBody:
      "Use at least 12 characters mixing letters, numbers and symbols. Store it in a password manager.",
    forgotTitle: "Forgot your current password?",
    forgotBody:
      "Sign out and use the password reset flow on the login page — we'll email you a link.",
    forgotCta: "Reset password",
    hintMinChars: "At least 8 characters",
    updating: "Updating…",
    deleteEyebrow: "Account · VIII",
    deleteDesc:
      "Permanently remove your account, enrolments and saved content. This cannot be undone.",
    permanentEyebrow: "Permanent action",
    cannotUndoTitle: "This step cannot be undone.",
    whatGetsRemoved: "What gets removed",
    removedEnrolments: "Course enrolments and progress",
    removedSaved: "Saved modules and bookmarks",
    removedCredentials: "Your sign-in credentials",
    keepCertificatesHint:
      "If you'd like to keep your certificates, download them before requesting deletion.",
    deleteWord: "DELETE",
    confirmationLabel: "Confirmation",
    inFieldBelow: "in the field below.",
    working: "Working…",
    membershipEyebrow: "Membership · IV",
    yourMembership: "Your membership",
    unlockAcademy: "Unlock the academy",
    membershipManageDesc: "Manage your access. Renewals are automatic; you can cancel any time.",
    membershipPitchDesc:
      "One membership opens every course, podcast, video and event below. Cancel any time — no hidden fees.",
    subscribeThanks:
      "Thanks for subscribing — activation may take a few seconds. Refresh if you don't see it yet.",
    subscriptionActivating: "Activating your membership — this usually takes a few seconds.",
    subscriptionActivatingSlow:
      "Your payment went through, but the confirmation is taking longer than usual. This page updates on its own; if nothing changes within a minute, refresh it.",
    plansEyebrow: "Plans · V",
    switchYourPlan: "Switch your plan",
    pickPlanHint: "Pick a plan to continue with Stripe checkout.",
    cancelledLabel: "Cancelled",
    whatYouGet: "What you get",
    everythingInOne: "Everything in one membership.",
    libraryTally:
      "Full access to the entire library — {count} pieces of content and counting. New courses, podcasts and events drop every month.",
    libraryEvents: "Live events",
    libraryVideos: "Video library",
    cancelAnytimeShort: "Cancel anytime",
    membershipPerks: [
      { title: "Unlimited access", body: "Every course, every podcast." },
      { title: "New content monthly", body: "Library keeps growing." },
      { title: "Cancel anytime", body: "Keep access until period ends." },
      { title: "Secure checkout", body: "Payments handled by Stripe." },
    ],
    trustBand: [
      "Powered by Stripe",
      "256-bit encrypted checkout",
      "No hidden fees",
      "Cancel in one click",
    ],
    faqEyebrow: "Questions · VI",
    faqTitle: "Frequently asked",
    membershipFaq: [
      {
        q: "Can I cancel anytime?",
        a: "Yes. Cancel from this page in one click. You keep access until the end of your billing period.",
      },
      {
        q: "What happens to my progress if I cancel?",
        a: "Your account, enrolments and saved content stay. Reinstate the subscription and pick up exactly where you left off.",
      },
      {
        q: "Do I need separate purchases for courses, podcasts and events?",
        a: "No. One membership unlocks the entire library — courses, podcasts, videos and live events.",
      },
      {
        q: "How is my payment secured?",
        a: "All payments are processed by Stripe over an encrypted connection. We never see or store your card details.",
      },
      {
        q: "Can I switch plans later?",
        a: "Yes. Switch between plans from this page anytime; you'll be prorated for the change.",
      },
    ],
    readyWhenYouAre: "Ready when you are",
    pickPlanAbove: "Pick a plan above to get started.",
    backToPlans: "Back to plans",
    membershipLabel: "Membership",
    noPlansConfigured: "No plans configured.",
    switchPlan: "Switch plan",
    savePct: "Save {pct}%",
    mostPopular: "Most popular",
    monthlyEquivalent: "That's {amount} / month",
    reinstatedNotice: "Subscription reinstated.",
    cancelledNotice: "Subscription cancelled — you’ll keep access through the current period.",
    planPriceUnavailable: "That plan price isn’t available.",
    stripePreviewOnly: "Stripe isn't configured for this portal. Pricing preview only.",
    payWithCard: "Pay with card",
    addStripeKey: "Add your Stripe publishable key to enable card collection.",
    couponOptional: "Optional",
  },
  collections: {
    navLabel: "Collections",
    typeLabel: "Collection",
    indexTitle: "Collections",
    indexDesc: "Topics and learning paths, put together by the {portal} team.",
    groupedSection: "Topics",
    orderedSection: "Learning paths",
    itemCount: "Items: {n}",
    extras: "Also in this collection",
    seeAll: "See all ({n})",
    step: "Step {n}",
    empty: "No collections yet.",
    emptyCollection: "Nothing in this collection is available to you yet.",
  },
  faq: {
    navLabel: "FAQ",
    typeLabel: "FAQ",
    indexTitle: "Frequently asked questions",
    indexDesc: "Quick answers from the {portal} team.",
    otherSection: "Other questions",
    related: "Related questions",
    allQuestions: "All questions",
    openPage: "Open as a page",
    signInToRead: "Sign in to read the answer.",
    notAvailable: "This answer isn't available with your account.",
    empty: "No questions yet.",
    askAssistant: "Didn't find your answer? Ask the assistant.",
    searchPlaceholder: "Search the questions…",
    noMatchTpl: "No question matches “{q}”.",
  },
  issues: {
    reportProblem: "Report a problem",
    navLabel: "My reports",
    cardBody: "Tell the team what isn't working and follow their replies.",
    eyebrow: "Support",
    listDesc: "Problems you've reported to the {portal} team, and their replies.",
    emptyTitle: "No reports yet",
    emptyHint: "If something isn't working, tell us — the team's reply will appear here.",
    newReport: "New report",
    updated: "New reply",
    disabledNotice: "This portal isn't taking new reports right now.",
    formDesc: "Your report goes to the {portal} team. Their reply will appear under My reports.",
    descriptionLabel: "What happened?",
    descriptionPlaceholder: "What did you do, what did you expect, and what happened instead?",
    pageLabel: "Page",
    filesLabel: "Screenshots or files",
    filesHint: "Optional. Images, PDF or text, 10 MB in total.",
    filesLeft: "Files left on this report: {n}",
    addFiles: "Add files",
    removeFile: "Remove {name}",
    fileType: "Only images (PNG, JPG, WebP, GIF), PDF or text files.",
    fileCount: "A report can hold at most 5 files.",
    fileSize: "Files together can be at most 10 MB.",
    descriptionRequired: "Describe what happened.",
    submit: "Send report",
    sending: "Sending…",
    sent: "Thanks — your report has reached the team.",
    viewReport: "View report",
    closePanel: "Close",
    reportedOn: "Reported {date}",
    closedOn: "Closed {date}",
    you: "You",
    team: "Team",
    noReplies: "No replies yet — the team will answer here.",
    replyLabel: "Reply",
    replyPlaceholder: "Add details or answer the team…",
    replyRequired: "Write a reply first.",
    sendReply: "Send reply",
    reopenHint: "Replying reopens this report.",
    closedHint: "This report is closed. You can still add a reply.",
    backToList: "All reports",
    download: "Download {name}",
    category: {
      BUG: "Something's broken",
      CONTENT: "A content error",
      FEATURE: "An idea",
      QUESTION: "A question",
    },
    status: {
      OPEN: "Open",
      IN_PROGRESS: "In progress",
      RESOLVED: "Resolved",
      CLOSED: "Closed",
    },
  },
  units: {
    course: { one: "course", two: "courses", few: "courses", other: "courses" },
    lesson: { one: "lesson", two: "lessons", few: "lessons", other: "lessons" },
    section: { one: "section", two: "sections", few: "sections", other: "sections" },
    episode: { one: "episode", two: "episodes", few: "episodes", other: "episodes" },
    item: { one: "item", two: "items", few: "items", other: "items" },
    step: { one: "step", two: "steps", few: "steps", other: "steps" },
  },
  errors: {
    generic: "Something went wrong.",
    genericBody: "We hit a snag loading this view.",
    tryAgain: "Try again",
    notFound: "Page not found",
    notFoundBody: "We couldn’t find the page you were looking for.",
    backHome: "Back to home",
    languageNotAvailable: "This page is not available in English.",
    unauthorized: "Please sign in to continue.",
    userPasswordMismatch: "Email or password is incorrect.",
    userDisabled: "This account is disabled. Contact support.",
    codeNotValid: "This link is no longer valid.",
    codeConfirmed: "This link has already been used.",
    passwordMismatch: "Current password is incorrect.",
    alreadyEnrolled: "You're already enrolled.",
    courseAccessNotAllowed: "Enroll in the course to continue.",
    assignmentGraded: "This answer has been graded and can no longer be edited.",
    quizSubmitFailed: "Something went wrong submitting the quiz. Please reload and try again.",
    moduleNotEnrollable: "This module isn't enrollable.",
    moduleRequiresPayment: "This course is paid — add it to the cart to get access.",
    moduleNotPurchasable: "This module isn't available for individual purchase.",
    cartEmpty: "Your cart is empty or its items are no longer available.",
    moduleNotFound: "Module not found.",
    moduleFree: "This module is free — no payment required.",
    moduleNoPrice: "This module isn't sold individually.",
    moduleAlreadyOwned: "You already own this module.",
    couponInvalid: "This coupon isn't valid for the selected plan.",
    couponExpired: "This coupon has expired.",
    subscriptionNotFound: "No subscription found.",
    subscriptionAlreadyCancelled: "Subscription is already cancelled.",
    subscriptionNotCancelled: "Subscription isn't cancelled.",
    socialCancelled: "That sign-in was cancelled.",
    socialStateInvalid: "That sign-in link expired. Please try again.",
    socialExchangeFailed: "That sign-in didn't complete. Please try again.",
    socialProviderDisabled: "That sign-in method isn't available on this site.",
    // Common on Facebook accounts opened with a phone number rather than an
    // address — there is nothing for us to identify them by.
    socialEmailMissing:
      "That account didn't share an email address. Please register with an email.",
    // Deliberate refusal, not a fault: adopting an unconfirmed local account
    // is how someone else's address becomes someone else's account.
    socialAccountUnverified:
      "Confirm your email first, or sign in with your password and connect this account from your profile.",
    socialIdentityTaken: "That account is already connected to a different profile here.",
    socialLastCredential:
      "That's the only way into your account. Set a password before disconnecting it.",
    issueReportsDisabled: "This portal isn't taking new reports right now.",
    issueReportNotFound: "That report isn't available.",
    issueReportRateLimited: "You've sent a lot in the last hour. Please try again later.",
    issueReportTooManyAttachments: "A report can hold at most 5 files.",
    emailRequired: "Email is required.",
    emailPasswordRequired: "Email and password are required.",
    nameSurnameRequired: "Name and surname are required.",
    passwordMinLength: "Password must be at least 8 characters.",
    passwordsDontMatch: "Passwords don’t match.",
    accountExists: "An account with this email already exists — sign in instead.",
  },
  journey: {
    welcomeAboard: "Welcome aboard",
    welcomeBack: "Welcome back",
    yourJourney: "Your learning journey",
    startsHere: "starts here",
    inProgress: "in progress",
    nextUpForYou: "Next up for you",
    tagline: "Continue at your own pace.",
    taglineExplore: "A curated selection to begin with.",
    startWithThese: "A selection to begin",
    recommendedSelection: "A curated selection:",
    activeCourses: "Active courses",
    moreInLibrary: "More in the library",
    continueModule: "Continue",
    openModule: "Open",
    beginModule: "Begin",
    seeAll: "See all",
    recommendedForYou: "Recommended for you",
    expandYourLibrary: "Expand your library.",
    alsoInLibrary: "Also in the library",
    andThen: "And then",
  },
  dashboard: {
    latestNews: "Latest news",
    allNews: "All news",
    browseCatalog: "Browse the catalog",
    completedLabel: "Completed",
    newInAcademy: "New in the Academy",
    allDocuments: "All documents",
    continueLearning: "Continue learning",
    noNews: "No news yet.",
    newThisWeek: "New this week",
    featured: "Featured",
    comingUp: "Coming up",
  },
  brain: {
    // `{name}` is `assistant.brandName` — the ONE cell an admin renames the
    // assistant in (and `Portal.aiAssistantName` beats it when set, getDict). This used to repeat the name as plain text, so a portal that
    // renamed it (dev: "Ava") kept greeting members with the old name here.
    askTitle: "Ask {name}",
    askPlaceholder: "Ask anything about your company knowledge…",
    askAction: "Ask",
    suggestions: [
      "What training is available?",
      "Summarize the latest news",
      "What's new this month?",
    ],
    knowledgeBase: "Knowledge base",
    recentlyAdded: "Recently added",
    documents: "Documents",
    recordings: "Recordings",
    training: "Training",
    podcasts: "Podcasts",
  },
};

const dicts: Record<string, Dict> = { EN: en };

export function dict(lang: Lang): Dict {
  // EN is the ONLY baked dictionary (D19) — source language, key authority,
  // and universal fallback. Every delivery language (sl_SI included) arrives
  // via the admin `translations` overlay (getDict / DictProvider) on top of
  // this baked EN base; an untranslated key therefore renders English rather
  // than blank. The lookup stays defensive for unmapped `lang` values (some
  // Next 16 render path was reaching page.tsx with one, O8). The Slovenian
  // chrome corpus lives on as DATA in lib/i18n-packs/sl_SI.json — the
  // manifest's new-portal seed, not a runtime dictionary.
  return dicts[lang] ?? en;
}

/**
 * A lang-relative path → the URL in `lang` ("/courses" → "/SI/courses").
 *
 * Two shapes authors write for in-page section anchors (lib/pages/section-frame.ts):
 * a bare "#cena" stays as it is — a jump on the page the link is on, no
 * navigation — and the locale root keeps its URL with a fragment or query
 * after it ("/#cena" → "/SI#cena", never "/SI/#cena", which costs a
 * trailing-slash redirect).
 */
export function langPath(lang: Lang, path: string): string {
  if (path.startsWith("#")) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `/${lang}${/^\/(?=[?#]|$)/.test(normalized) ? normalized.slice(1) : normalized}`;
}

export function swapLang(currentPath: string, newLang: Lang): string {
  const parts = currentPath.split("/").filter(Boolean);
  if (parts[0] && isLangSegment(parts[0])) parts[0] = newLang;
  else parts.unshift(newLang);
  return "/" + parts.join("/");
}

/**
 * A query string carried across a language switch ("?redirect=/SI/dashboard"
 * → "?redirect=/EN/dashboard"). The sign-in page's `redirect` is a path in
 * the old language, so keeping it verbatim signed an English reader into the
 * Slovenian dashboard (2026-10-19 QA). Only a local path that starts with a
 * locale segment is rewritten; everything else passes through untouched.
 */
export function swapLangSearch(search: string, newLang: Lang): string {
  if (!search) return "";
  const params = new URLSearchParams(search);
  const redirect = params.get("redirect");
  if (redirect && /^\/[A-Z]{2}(?=[/?#]|$)/.test(redirect)) {
    params.set("redirect", `/${newLang}${redirect.slice(3)}`);
  }
  const out = params.toString();
  return out ? `?${out}` : "";
}
